// ベンダー別マッパー（中身/TMS）。CSV1行 → Trip形（schema.js Trip ＋ 取込メタ）。
// すべて「ヘッダ名解決」。トランコムの位置依存（列ズレ）を廃し、列名で参照する。
// 出所: GAS「完全統合版」VENDOR_CONFIGS.mapRow を本体へ移植。①一意キー keyOf ／ ②可変項目 signatureFields を各社定義。
import {
  normalizeName, isAceJapan, toISODate, lastNumberGroup, stripPhone,
  parseMoney, firstNonEmpty, vehicleClass, joinPlace,
} from './helpers.js'

const DASH = /^[ー－ｰ−\-—–]+$/ // 「発注案件（荷主なし）」の合図
// ハコベル案件番号（＝ハコベル発行の管理NO）の形: YYMMDD-xx-xxx（例 260731-02K-RXN）。
// 日計表 備考①（請求メモ）がこの形なら「ハコベル便」と判定する。電話番号や一般メモは弾く。
const HAKO_NO = /^\d{6}-[0-9A-Za-z]{1,6}-[0-9A-Za-z]{1,6}$/

// 候補ヘッダ名から最初の非空値（CSV書式差の吸収）。
const pickCol = (r, names) => { for (const n of names) { const v = r[n]; if (v != null && String(v).trim() !== '') return v } return '' }
// 運賃：空欄・「仮」は 0 円確定にせず provisional。金額があれば通常の数値。
function takeFare(raw) {
  const empty = raw == null || String(raw).trim() === ''
  const v = parseMoney(raw)
  return { value: v == null ? 0 : v, provisional: v == null || empty }
}
const toInt = (v) => { const n = parseInt(String(v == null ? '' : v).replace(/[^\d]/g, ''), 10); return Number.isFinite(n) ? n : null }
// 日計表の候補列（全社集計エクスポート／生の担当別日計表の両対応）
const DCOL = {
  id: ['管理ID'], date: ['運行日'], unload: ['卸日'], staff: ['担当'],
  cust: ['得意先', '得意先名'], from: ['発地', '発'], to: ['着地', '着'], item: ['品名'], klass: ['車種'],
  own: ['自社乗務員'], partner: ['協力会社', '協力会社名'], carrier: ['実運送会社', '実運送'],
  fare: ['運賃'], ycost: ['傭車金額', '傭車料'], tollBill: ['請通行料', '請求通行料'], tollPay: ['支通行料', '支払通行料'],
  ycharter: ['傭車便'], custCd: ['得意先CD', '得意先コード'], custEda: ['得枝番', '得意先枝番'],
  partCd: ['協力会社CD', '協力会社コード'], partEda: ['協枝番', '協力会社枝番'], drvCd: ['社員コード', '乗務員コード', '社員CD'],
  memo1: ['備考①請求', '備考①', '備考1請求', '備考１請求', 'ハコベル管理NO', '管理NO'],
}

// 取込Trip の共通ひな形（未使用項目も明示）。schema.js Trip に整合。
function baseTrip(source) {
  return {
    source, sourceKey: '', ankenNo: '',
    manageId: '', date: '', unloadDate: '', month: '', billMonth: '',
    staff: '', customerName: '', partnerName: '', driverName: '',
    kind: 'own', vehicleId: '', vehicleClass: '',
    origin: '', dest: '', item: '', memo: '', tel: '', shipper: '',
    fare: 0, charterCost: 0, tollCost: 0, tollBill: 0, tollPay: 0, hasToll: false,
    status: '', billStatus: '', payStatus: '',
    provisional: false, // 「仮」運賃など未確定
  }
}

// 月キー（YYYY-MM）。ISO日付の先頭7桁。
const ym = (iso) => (iso && iso.length >= 7 ? iso.slice(0, 7) : '')

// ---- ① 配車担当 日計表（全社運行データ）: 管理IDが無い版は複合キー ----
const daily = {
  id: 'daily',
  label: '配車担当 日計表',
  encoding: 'auto',
  target: 'trips',
  idColumn: '管理ID', // 生の担当別日計表なら存在。全社集計エクスポートには無い→複合キー。
  signatureFields: ['fare', 'charterCost', 'tollBill', 'tollPay', 'kind', 'driverName', 'partnerName', 'vehicleClass'],
  keyOf(r) {
    const id = String(pickCol(r, DCOL.id)).trim()
    if (id) return id
    // 複合キー（可変の金額は含めない＝②で変更検知させるため）
    return [
      toISODate(pickCol(r, DCOL.date), null) || pickCol(r, DCOL.date),
      pickCol(r, DCOL.staff), pickCol(r, DCOL.cust), pickCol(r, DCOL.from), pickCol(r, DCOL.to),
      firstNonEmpty(pickCol(r, DCOL.own), pickCol(r, DCOL.partner)),
    ].map((x) => normalizeName(x)).join('|')
  },
  map(r, ctx = {}) {
    const t = baseTrip('daily')
    const iso = toISODate(pickCol(r, DCOL.date), ctx.year) || String(pickCol(r, DCOL.date)).trim()
    const own = pickCol(r, DCOL.own), partner = pickCol(r, DCOL.partner), carrier = pickCol(r, DCOL.carrier)
    const flag = normalizeName(pickCol(r, DCOL.ycharter))
    // 区分: 傭車便フラグ（全社集計）優先。無ければ生の日計表を 乗務員/協力会社/実運送 の有無で3分類。
    let kind
    if (flag === 'はい') kind = 'charter'
    else if (flag === 'いいえ') kind = 'own'
    else {
      const hasOwn = String(own).trim() !== ''
      const hasPart = String(partner).trim() !== '' || String(carrier).trim() !== ''
      kind = (hasOwn && !hasPart) ? 'own' : (hasPart ? 'charter' : 'other') // other＝乗務員も協力会社も無い＝収支対象外
    }
    t.date = iso; t.month = ym(iso)
    t.unloadDate = toISODate(pickCol(r, DCOL.unload), ctx.year)
    t.staff = pickCol(r, DCOL.staff) || ''
    t.customerName = pickCol(r, DCOL.cust) || ''
    t.origin = pickCol(r, DCOL.from) || ''; t.dest = pickCol(r, DCOL.to) || ''
    t.item = pickCol(r, DCOL.item) || ''
    t.vehicleClass = vehicleClass(pickCol(r, DCOL.klass))
    t.kind = kind
    t.driverName = own || ''
    if (kind === 'charter') t.partnerName = firstNonEmpty(partner, carrier)
    const fare = takeFare(pickCol(r, DCOL.fare))
    t.fare = fare.value
    if (fare.provisional) t.provisional = true
    const ycost = parseMoney(pickCol(r, DCOL.ycost))
    if (ycost == null) { t.charterCost = 0; t.provisional = true } else t.charterCost = ycost
    t.tollBill = parseMoney(pickCol(r, DCOL.tollBill)) || 0
    t.tollPay = parseMoney(pickCol(r, DCOL.tollPay)) || 0
    t.tollCost = kind === 'charter' ? t.tollPay : t.tollBill
    t.hasToll = (t.tollBill + t.tollPay) > 0
    // 生の日計表にコード類があれば取込（デジタコ突合・名寄せに使用）
    t.driverCode = String(pickCol(r, DCOL.drvCd)).trim()
    const cc = toInt(pickCol(r, DCOL.custCd)); if (cc != null) { t.customerCode = cc; const ce = toInt(pickCol(r, DCOL.custEda)); if (ce != null) t.customerBranchNo = ce }
    const pc = toInt(pickCol(r, DCOL.partCd)); if (pc != null) { t.partnerCode = pc; const pe = toInt(pickCol(r, DCOL.partEda)); if (pe != null) t.partnerBranchNo = pe }
    // 備考①（請求メモ）にハコベル管理NO（案件番号）が入っていれば取込＝突合/選別キー。
    const m1 = String(pickCol(r, DCOL.memo1)).trim()
    if (m1 && HAKO_NO.test(m1)) t.ankenNo = m1
    t.manageId = id2(r)
    return t
  },
  // 日計表取込の除外（＝確定取込しない）。二重計上を防ぐ安全網。ctx は ingest 画面から供給。
  //  (a) 既にハコベルCSVから取込済みの案件（自社便/傭車問わず）＝ハコベルCSVが正。ctx.hakoAnken(既存案件番号Set)。
  //  (b) ハコベル自社便（備考①=管理NO かつ 自社便）＝ハコベルCSVが正（CSV未取込でも日計表からは入れない）。
  //  (c) 移行済担当×運行日≥切替日＝VEIL入力が正。ctx.migratedStaff({担当:切替日YYYY-MM-DD})。
  //  (d) VEIL直接入力（手入力/定期便）と 日付+乗務員+運賃 が一致＝VEIL入力が正（同一便を日計表から二重取込しない）。ctx.veilSig(既存VEIL直接入力の署名Set)。
  excludeOf(t, r, ctx = {}) {
    // (d) VEIL直接入力（手入力/定期便）と一致＝VEIL入力が正・日計表からは取り込まない（管理IDが別体系でも署名で吸収）。
    //     照合の当事者＝自社便は乗務員／傭車便は協力会社（傭車は自社乗務員が空のため協力会社で照合）。
    if (ctx.veilSig && t.date && Number(t.fare) > 0 && t.kind !== 'other') {
      const party = String(t.driverName || '').trim() || String(t.partnerName || '').trim()
      if (party) {
        const vsig = `${t.date}|${party.replace(/\s+/g, '')}|${Math.round(Number(t.fare))}`
        if (ctx.veilSig.has(vsig)) return `VEIL直接入力済みと一致（${t.date}／${party}／¥${Math.round(Number(t.fare)).toLocaleString('ja-JP')}）＝日計表取込対象外（二重計上防止）`
      }
    }
    // 管理IDが無い行は取り込まない（重複防止＝日計表は管理ID必須）。※管理ID列ごと無いファイルは全行対象になる点に注意。
    if (!t.manageId) return '管理IDなし＝取込対象外（重複防止のため管理ID必須）'
    const anken = t.ankenNo
    if (anken && ctx.hakoAnken && ctx.hakoAnken.has(anken)) return `ハコベル取込済（案件番号 ${anken}）＝日計表取込対象外`
    if (t.kind === 'own' && anken) return `ハコベル自社便（管理NO ${anken}）＝日計表取込対象外`
    const cut = (ctx.migratedStaff && t.staff) ? ctx.migratedStaff[t.staff] : ''
    if (cut && t.date && String(t.date) >= String(cut)) return `移行済担当（${t.staff}）切替日${cut}以降＝VEIL入力が正・日計表取込対象外`
    return ''
  },
  // 警告（取込は行うが要確認）。備考①に管理NO未記入でも、既存ハコベル便と 日付+乗務員+運賃 が一致すれば
  // 「ハコベル便の入れ忘れ＝二重計上の恐れ」を検知して知らせる。ctx.hakoSig(既存ハコベルの署名Set)。
  warnOf(t, r, ctx = {}) {
    if (t.ankenNo) return ''
    const sig = `${t.date || ''}|${String(t.driverName || '').replace(/\s+/g, '')}|${Math.round(Number(t.fare) || 0)}`
    if (ctx.hakoSig && ctx.hakoSig.has(sig)) return 'ハコベル便の可能性（備考①に管理NO未記入・既存ハコベル便と日付+乗務員+運賃が一致）'
    return ''
  },
}
function id2(r) { return String(pickCol(r, DCOL.id)).trim() }

// ---- ② トランコム 取引状況CSV（95列・IDなし）: 複合キー ----
const trancom = {
  id: 'trancom',
  label: 'トランコム',
  encoding: 'utf-8', // UTF-8(BOM)
  target: 'trips',
  signatureFields: ['fare', 'tollCost', 'kind', 'driverName', 'partnerName', 'vehicleId'],
  keyOf(r) {
    const dealDate = normalizeName(r['取引決定日']).replace(/\(.*?\)/g, '') // 曜日カッコ除去
    const loadDone = normalizeName(r['積完']) // 積完タイムスタンプ＝物理運行の一意印
    const parts = [
      dealDate,
      toISODate(r['積み日'], null),
      normalizeName(r['積み地']), normalizeName(r['降し地']),
      normalizeName(r['乗務員']),
      loadDone, // 空なら下で無視されるだけ
    ]
    return parts.map((x) => x || '').join('|')
  },
  map(r, ctx = {}) {
    const t = baseTrip('trancom')
    const iso = toISODate(r['積み日'], ctx.year)
    const own = isAceJapan(r['看板名'])
    t.date = iso; t.month = ym(iso)
    t.unloadDate = toISODate(r['降し日'], ctx.year)
    // センター名で得意先を区別（例: トランコム京都）。※列があれば併記情報として保持。
    t.customerName = 'トランコム' + firstNonEmpty(r['センター名'], r['センター名※'])
    t.origin = r['積み地'] || ''; t.dest = r['降し地'] || ''
    t.item = firstNonEmpty(r['品名'], r['貨物'])
    t.kind = own ? 'own' : 'charter'
    const driver = firstNonEmpty(r['乗務員※'], r['乗務員'])
    if (own) t.driverName = driver
    else { t.driverName = driver; t.partnerName = normalizeName(r['看板名']) }
    t.vehicleId = lastNumberGroup(firstNonEmpty(r['車番※'], r['車番']))
    t.tel = stripPhone(r['携帯番号'])
    // ※(変更後) を優先。「仮」なら provisional。
    const fare = parseMoney(firstNonEmpty(r['運賃※'], r['運賃']))
    if (fare == null) { t.fare = 0; t.provisional = true } else t.fare = fare
    // 高速: 通行料※ → その他 → 通行料（GASは その他 を優先採用）
    const toll = parseMoney(firstNonEmpty(r['通行料※'], r['その他'], r['通行料']))
    t.tollCost = toll == null ? 0 : toll
    t.tollBill = t.tollCost; t.hasToll = t.tollCost > 0
    return t
  },
}

// ---- ③ ハコベル 案件CSV（132列）: 案件番号＝一意キー ----
const hacobell = {
  id: 'hacobell',
  label: 'ハコベル',
  encoding: 'shift_jis', // cp932
  target: 'trips',
  idColumn: '案件番号',
  signatureFields: ['fare', 'charterCost', 'tollCost', 'kind', 'driverName', 'partnerName', 'vehicleId', 'billStatus', 'payStatus'],
  keyOf(r) { return (r['案件番号'] || '').trim() },
  map(r, ctx = {}) {
    const t = baseTrip('hacobell')
    const anken = (r['案件番号'] || '').trim()
    const carrier = r['運送会社'] || ''
    const isHacchu = DASH.test(String(r['荷主会社名'] || '').trim())
    const own = !isHacchu && isAceJapan(carrier)
    const iso = toISODate(firstNonEmpty(r['開始：日付'], r['開始：期間の日付']), ctx.year)
    t.ankenNo = anken; t.memo = anken // 備考②（M列）に案件番号
    t.manageId = anken
    t.date = iso; t.month = ym(iso)
    t.unloadDate = toISODate(firstNonEmpty(r['終了：日付'], r['終了：期間の日付']), ctx.year)
    t.billMonth = ym(toISODate(r['請求：計上日'], ctx.year)) || t.month
    t.customerName = 'ハコベル'
    t.shipper = r['荷主会社名'] || ''
    t.origin = joinPlace(r['開始：住所（都道府県）'], r['開始：住所（市区町村）'])
    t.dest = joinPlace(r['終了：住所（都道府県）'], r['終了：住所（市区町村）'])
    t.item = r['品名'] || ''
    t.vehicleClass = vehicleClass(firstNonEmpty(r['車両サイズ'], r['希望車両'], r['車型']))
    t.kind = own ? 'own' : 'charter'
    if (own) {
      t.driverName = r['配送ドライバー'] || ''
      t.vehicleId = lastNumberGroup(r['配送車両'])
    } else {
      t.partnerName = carrier
      t.driverName = r['配送ドライバー'] || ''
    }
    // 請求(得意先向け): 完了後(変更後)→受注時→受注基本
    const fare = takeFare(firstNonEmpty(r['請求：完了後の小計'], r['請求：受注時の小計'], r['受注金額：基本料金']))
    t.fare = fare.value
    if (fare.provisional) t.provisional = true
    // 支払(協力会社向け, 傭車時のみ): 完了後→発注時→運賃基本
    if (!own) t.charterCost = parseMoney(firstNonEmpty(r['支払：完了後の小計'], r['支払：発注時の小計'], r['運賃：基本料金'])) || 0
    t.tollBill = parseMoney(r['請求：高速料金']) || 0
    t.tollPay = parseMoney(r['支払：高速料金']) || 0
    t.tollCost = own ? t.tollBill : t.tollPay
    const tollFlag = /別/.test(firstNonEmpty(r['受注金額：高速料金区分'], r['運賃：高速料金区分']))
    t.hasToll = tollFlag || t.tollCost > 0
    t.status = r['案件ステータス'] || ''
    t.billStatus = r['請求：ステータス'] || ''
    t.payStatus = r['支払：ステータス'] || ''
    return t
  },
}

// ---- ④ デジタコ（富士通ITP 日報集計データ・118列）: 運行ID＝一意キー → tachographs ----
// 出所: ace_tms_pipeline.load_digitaco（乗務員コード×運行期間 / 距離raw÷100=km）＋118列ヘッダ。
const digitaco = {
  id: 'digitaco',
  label: 'デジタコ（富士通ITP 日報集計）',
  encoding: 'shift_jis',
  target: 'tachographs', // trips ではなく tachographs（車番×日の実車率・実走行）
  idColumn: '運行ID',
  signatureFields: ['runKm', 'loadedKm', 'emptyKm', 'fuelLiter'],
  keyOf(r) {
    const id = (r['運行ID'] || '').trim()
    if (id && id !== '0') return id
    return [(r['乗務員コード'] || '').trim(), normalizeName(r['運行開始日時']), normalizeName(r['運行終了日時'])].join('|')
  },
  map(r, ctx = {}) {
    const km = (v) => { // 生値 ÷100 = km（小数1位）
      const n = parseFloat(String(v == null ? '' : v).replace(/[^\d.-]/g, ''))
      return Number.isFinite(n) ? Math.round(n / 100 * 10) / 10 : 0
    }
    const sec = (v) => { const n = parseFloat(String(v == null ? '' : v).replace(/[^\d.-]/g, '')); return Number.isFinite(n) ? n : 0 }
    let fuel = 0
    for (let i = 1; i <= 9; i++) { const n = parseFloat(String(r['燃料数量' + i] || '').replace(/[^\d.-]/g, '')); if (Number.isFinite(n)) fuel += n }
    const iso = toISODate(firstNonEmpty(r['運行開始日時'], r['運行日付']), ctx.year)
    return {
      source: 'digitaco', tripId: (r['運行ID'] || '').trim(),
      driverCode: (r['乗務員コード'] || '').trim(), driverName: r['乗務員名'] || '',
      plate: lastNumberGroup(firstNonEmpty(r['車両名称'], r['車両番号'])) || (r['車両番号'] || '').trim(),
      vehicleNo: (r['車両番号'] || '').trim(), vehicleName: r['車両名称'] || '',
      date: iso, month: iso.length >= 7 ? iso.slice(0, 7) : '',
      startAt: normalizeName(r['運行開始日時']), endAt: normalizeName(r['運行終了日時']),
      runKm: km(r['運転距離']), loadedKm: km(r['実車走行距離']), emptyKm: km(r['空車走行距離']),
      workSec: sec(r['稼働時間']), driveSec: sec(r['運転時間']),
      fuelLiter: Math.round(fuel * 10) / 10,
    }
  },
}

export const VENDORS = { daily, trancom, hacobell, digitaco }
export const VENDOR_LIST = [daily, trancom, hacobell, digitaco]
